import { Inject, Injectable } from "@angular/core";
import { AuctionsCategories, AuctionsPage } from "src/app/types/auctions";
import { environment } from "src/environments/environment";
import { SharedService } from "../shared-service/shared.service";
import { DOCUMENT } from "@angular/common";
import { CategoryList } from "../../components/category-list";
import { AuthService } from "../auth/auth.service";
import { TranslateService } from "../translation/translate.service";
import { CategoryTree, LanguageRegionMap, MachineBaseDto, QueryParams } from "src/app/types";
import { languageRegionCodesMap } from "../../constants";
import { ClientService } from "../client/client.service";
import { ExtraParams } from "../filter/filter.service";
import { ActivatedRouteSnapshot, Router } from "@angular/router";

@Injectable({
  providedIn: 'root',
})

export class AuctionsService {
  private hostName: string;
  regionCode!: string;
  constructor(
    private sharedService: SharedService,
    private authService: AuthService,
    private translateService: TranslateService,
    private router: Router,
    private client: ClientService,
    @Inject(DOCUMENT) private document: Document
  ) {
    
    if (this.sharedService.isBrowser()) {
      environment.host
        ? (this.hostName = environment.host)
        : (this.hostName = this.document.location.hostname);
    } else {
      this.hostName = this.sharedService.getRequestHostName();
    }
    this.regionCode = this.client.getRegionCode();
  }

  public getViewData():ActivatedRouteSnapshot['data'] {
    let route = this.router.routerState.snapshot.root;
    let out!:ActivatedRouteSnapshot['data'];
    while (route) {
      if (route.data && Object.keys(route.data).length) {
        out = route.data;
      }
      route = route.firstChild!;
    }
    return out;
  }

  public getCategory(): 'private' | 'public' {
    return this.getViewData()['type'].split('-')[0];
  }

  private getParams(params:ExtraParams) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((val) => searchParams.append(key, String(val)));
      } else {
        searchParams.append(key, String(value));
      }
    });
    return searchParams;
  }

  private getHeaders() {
    const accessToken = this.authService.getAccessToken() ?? '';
    const headers:any = {
      'Accept-Language': this.regionCode,
    };

    if (accessToken?.length) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
    return {
      ...(headers ?? {}),
    } as any
  }
  public getPage = async (category?: 'private' | 'public' ): Promise<AuctionsPage> => {
    if (!category) { category = this.getCategory(); }
    const path = `${environment.api}/client-web/client/${this.hostName}/${category}-auctions`;
    const response = await fetch(path, {
      headers: {
        'Accept-Language': this.regionCode,
      },
    });
    const data: AuctionsPage = await response.json();
    return data;
  }

  public getAuctions = async (category?: 'private' | 'public'): Promise<CategoryList[]> => {
    if (!category) { category = this.getCategory(); }
    const path = `${environment.api}/client-web-auctions/${this.hostName}/${category}`;
    const response = await fetch(path, {
        method: 'GET',
        headers: this.getHeaders(),
      }
    );

    const data: CategoryList[] = await response.json();
    return data;
  }

  public getClientTree = async(category: 'private' | 'public' | undefined, shortName: string, params?:ExtraParams): Promise<CategoryTree> => {
    if (!category) { category = this.getCategory(); }
    const query = params ? this.getParams(params) : '';

    let path;
    if (shortName === 'private' || shortName === 'public') {
      path = `${environment.api}/client-web-auctions/${this.hostName}/${category}/category-tree?${query.toString()}`;
    } else {
      path = `${environment.api}/client-web-auctions/${this.hostName}/${category}/${shortName}/category-tree?${query.toString()}`;
    }
    const response = await fetch(path, {
        method: 'GET',
        headers: this.getHeaders(),
      }
    );
    const data: CategoryTree = await response.json();
    return data;

  }
  public getMachineListUrl(
      category: 'private' | 'public' | undefined,
      shortName: string | null,
      lang: string,
      queryParams: QueryParams = {}
    ): string {
      if (!category) { category = this.getCategory(); }
      const searchParams = new URLSearchParams({ language: lang });

      if (queryParams instanceof URLSearchParams) {
        queryParams.forEach((value, key) => searchParams.append(key, value));
        queryParams = {};
      }

      Object.entries(queryParams).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((val) => searchParams.append(key, String(val)));
        } else {
          searchParams.append(key, String(value));
        }
      });
  
      const queryString = searchParams.toString()
        ? `?${searchParams.toString()}`
        : '';

      if (shortName === 'private' || shortName === 'public') {
        return `${environment.api}/client-web-auctions/${this.hostName}/${category}/machines/search${queryString}`;
      } else {
        return `${environment.api}/client-web-auctions/${this.hostName}/${category}/${shortName}/machines/search${queryString}`;
      }
  }

  public getMachineList(
    category: 'private' | 'public' | undefined,
    shortName: string | null,
    lang: string,
    queryParams: QueryParams = {}
  ) {
    const url = this.getMachineListUrl(category, shortName, lang, queryParams);
    return fetch(url, {
      method: 'GET',
      headers: this.getHeaders(),
    }).then(response => response.json());
  }

  public getSearchFiltersUrl(
      category: 'private' | 'public' | undefined,
      shortName: string | null,
      lang: string,
      queryParams: QueryParams = {}
    ): string {
      if (!category) { category = this.getCategory(); }
      const searchParams = new URLSearchParams({ language: lang });
  
      Object.entries(queryParams).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((val) => searchParams.append(key, String(val)));
        } else {
          searchParams.append(key, String(value));
        }
      });
  
      const queryString = searchParams.toString()
        ? `?${searchParams.toString()}`
        : '';

      if (shortName === 'private' || shortName === 'public') {
        return `${environment.api}/client-web-auctions/${this.hostName}/${category}/search-filters${queryString}`;
      }
      return `${environment.api}/client-web-auctions/${this.hostName}/${category}/${shortName}/search-filters${queryString}`;
    }

    public getNewSearchFiltersUrl(
      category: 'private' | 'public' | undefined,
      shortName: string | null,
      lang: string,
      queryParams: QueryParams = {}
    ): string {
      if (!category) { category = this.getCategory(); }
      const searchParams = new URLSearchParams({ language: lang });
  
      Object.entries(queryParams).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((val) => searchParams.append(key, String(val)));
        } else {
          searchParams.append(key, String(value));
        }
      });
  
      const queryString = searchParams.toString()
        ? `?${searchParams.toString()}`
        : '';

      return `${environment.api}/client-web-auctions/${this.hostName}/${category}/${shortName}/search-filters2${queryString}`;
    }

    public getMachineDetails = async (
        category: 'private' | 'public' | undefined,
        token: string | null,
        macId: string,
        shortName: string | null,
        options?: RequestInit
        ): Promise<MachineBaseDto> => {
        if (!category) { category = this.getCategory(); }

        const headers:any = options?.headers;
        if (token?.length) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        const response = await fetch(
            `${environment.api}/client-web-auctions/${this.hostName}/${category}/machines/${macId}`,
            {
              method: 'GET',
              headers: {
                ...(headers ?? {}),
              },
            }
        );
        const data: MachineBaseDto = await response.json();
        if(response.status === 404) {
            throw (response.status);
        }
        return data;
    };

}